import { useEffect, useRef, useState } from 'react';

import type { AccountStatusState } from '@src/models/interfaces/accountStatus.interface';
import { AccountStatusMapper } from '@src/models/mappers/AccountStatusMapper';
import { DEFAULT_ACCOUNT_STATUS_STATE } from '@src/packages/utils/hooks/useAccountStatus';
import AccountStatusService from '@src/services/AccountStatusService';
import { sendErrorToSentry } from '@src/util/sentry/utils';

/** How long the organization's screens wait for the first status before opening anyway. */
export const ACCOUNT_STATUS_WAIT_MS = 5000;

/**
 * Listens to the account status of `organizationId`. `loaded` turns true on the first snapshot,
 * on a listener error, or after {@link ACCOUNT_STATUS_WAIT_MS}; in the last two cases the account
 * stays writable: a missing or unreadable status must not lock a paying customer out.
 */
export const useAccountStatusSubscription = (
  organizationId: string | null | undefined,
): AccountStatusState => {
  const serviceRef = useRef<AccountStatusService | null>(null);
  if (serviceRef.current === null) serviceRef.current = new AccountStatusService();

  const [state, setState] = useState<AccountStatusState>(DEFAULT_ACCOUNT_STATUS_STATE);

  useEffect(() => {
    // Without an organization there is no status to wait for.
    if (!organizationId) {
      setState({ ...DEFAULT_ACCOUNT_STATUS_STATE, loaded: true });
      return undefined;
    }
    setState(DEFAULT_ACCOUNT_STATUS_STATE);

    const waitTimer = setTimeout(
      () => setState(current => (current.loaded ? current : { ...current, loaded: true })),
      ACCOUNT_STATUS_WAIT_MS,
    );
    const unsubscribe = serviceRef.current?.subscribe(
      organizationId,
      snapshot =>
        setState({
          snapshot,
          loaded: true,
          writable: AccountStatusMapper.isWritable(snapshot.status),
        }),
      error => {
        sendErrorToSentry(error, { context: 'useAccountStatusSubscription', organizationId });
        setState({ ...DEFAULT_ACCOUNT_STATUS_STATE, loaded: true });
      },
    );

    return () => {
      clearTimeout(waitTimer);
      unsubscribe?.();
    };
  }, [organizationId]);

  return state;
};
